/* eslint-disable max-len */
import compose from 'recompose/compose';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import LoadingBar from 'react-redux-loading-bar';
import get from 'lodash/get';

import withSidebarStateAndContext from '../common/hoc/withSidebarStateAndContext';
import { subscribe, unsubscribe } from '../common/pubnub';
import Main from '../main/main';
import Sidebar from '../sidebar/sidebar';
import { GLYPHS } from '../common/icon';
import withCollapsed from '../sidebar/withCollapsed';
import { containerApp } from '../../main';
import HeaderShopsContainer from '../sidebar/header/shopsContainer';
import {
  canUseEngage,
  canUserViewServices,
  canUserViewCustomers,
  canUserViewEngage,
  canUserViewReports,
  canUserViewDashboard,
  canUserViewProducts,
  canUserViewPromos,
  canUserViewBarbers,
  canUserViewStaffManagement,
} from '../common/helpers/users';
import { canActiveBrandUseMemberships, getFrontendFlags, getShopBrand } from '../auth/selectors';
import { getJobsStatus } from '../queue/actions';
import { PROMOS_BRAND_PATH } from '../../paths';
import { fetchBrandCampaign } from '../engagement/actions';
import { initializeEcommerceMatches } from '../products/products/Shopify/reducer/actions';
import BarberNavExplainer, { IS_BRAND_BARBERS_NEW } from '../main/barbers/containers/BarberNavExplainer';
import { BrandViewPopups } from '../../modules/shared/app-start-popups/brand-view-popups';
import config from '../../config';
import { t } from '../../modules/infra/i18n';

class BrandApp extends Component {
  constructor(props) {
    super(props);
    this.state = { marginClass: 'collapse' };
  }

  componentDidMount() {
    this.initNotificationListener();
  }

  componentWillUnmount() {
    unsubscribe(`brand_${this.props.brand.id}`);
  }

  initNotificationListener() {
    subscribe({
      channel: `brand_${this.props.brand.id}`,
      message: ({ message }) => {
        this.matches(message);
        this.queueJobs(message);
        this.campaign(message);
      },
    });
  }

  matches(args) {
    this.props.initializeMatches(this.props.brand.id, args, this.props.brand.id);
  }

  queueJobs(args) {
    const finishedJob = get(args, 'queues.finished');
    const failedJob = get(args, 'queues.failed');

    const job = finishedJob || failedJob;

    if (job) {
      this.props.getJobsStatus(job.queueName, job.jobName, { brandId: this.props.brand.id });
    }
  }

  campaign(args) {
    const campaign = get(args, 'campaign.updated');
    if (campaign) {
      this.props.fetchBrandCampaign(this.props.brand.id, campaign);
    }
  }

  render() {
    const data = [
      {
        id: 'reports',
        text: t('common:nav.reports'),
        iconName: GLYPHS.reports,
        url: '/brand/reports',
        show: this.props.canViewReports && this.props.canViewDashboard,
      },
      {
        id: 'reports-v2',
        text: t('common:nav.reports'),
        iconName: GLYPHS.reports,
        url: '/brand/reports/sales/v2',
        show: this.props.canViewReports && !this.props.canViewDashboard,
      },
      {
        id: 'customers',
        text: t('common:nav.clients'),
        iconName: GLYPHS.customers,
        url: '/brand/customers',
        show: this.props.customersEnabled,
      },
      {
        id: 'barbers',
        text: t('common:nav.staff'),
        iconName: GLYPHS.barbers,
        url: this.props.barbersEnabled ? '/brand/barbers' : '/brand/staff',
        show: this.props.barbersEnabled || this.props.staffManagementEnabled,
        label: {
          when: IS_BRAND_BARBERS_NEW,
          text: t('common:nav.new_label'),
        },
        explainer: {
          when: !this.props.frontendFlags.brandBarbersOnboarded && IS_BRAND_BARBERS_NEW,
          component: BarberNavExplainer,
        },
      },
      {
        id: 'services',
        text: t('common:nav.services'),
        iconName: GLYPHS.services,
        url: '/brand/services',
        show: this.props.servicesEnabled,
      },
      {
        id: 'products',
        text: t('common:nav.products'),
        iconName: GLYPHS.packages,
        url: '/brand/products',
        show: this.props.canViewProducts,
      },
      {
        id: 'engage',
        text: t('common:nav.engage'),
        iconName: GLYPHS.engagement,
        url: '/brand/engagement',
        show: this.props.engageEnabled,
      },
      {
        id: 'discounts',
        text: t('common:nav.promo_codes_loyalty_discounts'),
        iconName: GLYPHS.discounts,
        url: PROMOS_BRAND_PATH,
        show: this.props.canViewPromos,
      },
      {
        id: 'settings',
        text: t('common:nav.settings'),
        iconName: GLYPHS.settings,
        url: '/brand/settings',
        show: true,
      },
      {
        id: 'memberships',
        text: t('common:nav.memberships'),
        iconName: GLYPHS.memberships,
        url: '/brand/memberships',
        show: this.props.membershipsEnabled,
      },
      {
        id: 'help',
        text: t('common:nav.help_center'),
        iconName: GLYPHS.help,
        href: config.salesForceHelp.url,
        show: true,
      },
      {
        id: 'referrals',
        text: t('common:nav.referrals'),
        iconName: GLYPHS.referral,
        href: 'https://getsqr.co/referral',
        linkAttrs: { target: '_blank' },
        show: true,
      },
    ];

    const className = classnames({
      collapse: this.props.collapsed,
      expand: !this.props.collapsed,
      container: containerApp.isActive(),
    });

    return (
      <div>
        <LoadingBar className="c-loading-bar" />
        <Sidebar
          navData={data}
          collapsed={this.props.collapsed}
          setCollapsed={this.props.setCollapsed}
          display-if={!containerApp.isActive()}
        >
          <HeaderShopsContainer collapsed={this.props.collapsed && !this.props.sidebarActive} />
        </Sidebar>
        <Main marginClass={className}>{this.props.children}</Main>
        <BrandViewPopups />
      </div>
    );
  }
}

BrandApp.propTypes = {
  brand: PropTypes.object.isRequired,
  canViewDashboard: PropTypes.bool.isRequired,
  canViewPromos: PropTypes.bool.isRequired,
  canViewReports: PropTypes.bool.isRequired,
  canViewProducts: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
  collapsed: PropTypes.bool.isRequired,
  setCollapsed: PropTypes.func.isRequired,
  sidebarActive: PropTypes.bool.isRequired,
  engageEnabled: PropTypes.bool.isRequired,
  servicesEnabled: PropTypes.bool.isRequired,
  customersEnabled: PropTypes.bool.isRequired,
  membershipsEnabled: PropTypes.bool.isRequired,
  getJobsStatus: PropTypes.func.isRequired,
  initializeMatches: PropTypes.func.isRequired,
  barbersEnabled: PropTypes.bool.isRequired,
  staffManagementEnabled: PropTypes.bool.isRequired,
  frontendFlags: PropTypes.shape({
    brandBarbersOnboarded: PropTypes.bool,
  }).isRequired,
  fetchBrandCampaign: PropTypes.func.isRequired,
};

export default compose(
  connect(
    (state) => ({
      brand: getShopBrand(state),
      canViewReports: canUserViewReports(state.auth.permissions),
      canViewProducts: canUserViewProducts(state.auth.permissions),
      canViewPromos: canUserViewPromos(state.auth.permissions),
      canViewDashboard: canUserViewDashboard(state.auth.permissions),
      engageEnabled: canUseEngage(state) && canUserViewEngage(state.auth.permissions),
      servicesEnabled: canUserViewServices(state.auth.permissions),
      customersEnabled: canUserViewCustomers(state.auth.permissions),
      membershipsEnabled: canActiveBrandUseMemberships(state),
      barbersEnabled: canUserViewBarbers(state.auth.permissions),
      staffManagementEnabled: canUserViewStaffManagement(state),
      frontendFlags: getFrontendFlags(state),
    }),
    {
      getJobsStatus,
      initializeMatches: initializeEcommerceMatches,
      fetchBrandCampaign,
    }
  ),
  withSidebarStateAndContext,
  withCollapsed
)(BrandApp);
